<template>
  <v-container fluid py-0>
    <template v-if="expansionPanel">
      <h3 v-if="showLabel">Filters</h3>
      <v-row align="end" justify="start">
        <v-col
          cols="12"
          :sm="cols"
          class="pt-0"
          v-for="filter in filtersList.filter((f) => f.main)"
          :key="filter.id"
        >
          <component
            :key="filter.id"
            :is="filter.component"
            v-bind="filter.props"
            @onInput="onFilterInput($event, filter.id)"
            @onClear="onClearFilter($event, filter.id)"
          ></component>
        </v-col>
      </v-row>
      <v-expansion-panels v-model="panel">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <template v-if="expansionPanel"> More Filters </template>
            <h4 v-else class="subtitle-1">
              <v-icon>mdi-magnify</v-icon>{{ label }}
            </h4>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row align="center">
              <v-col
                cols="12"
                :sm="cols"
                v-for="filter in filtersList.filter((f) => !f.main)"
                :key="filter.id"
              >
                <component
                  :key="filter.id"
                  :is="filter.component"
                  v-bind="filter.props"
                  @onInput="onFilterInput($event, filter.id)"
                  @onClear="onClearFilter($event, filter.id)"
                ></component>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-row class="justify-start" v-if="selectedFilters.length > 0">
        <v-col cols="auto">
          <v-chip
            v-for="filter in selectedFilters"
            :key="filter.id"
            close
            @click:close="onFilterRemove(filter)"
            class="mr-1"
          >
            {{ filter.label }}:
            <strong>&nbsp;{{ filter.value }}</strong>
          </v-chip>
        </v-col>
        <v-col>
          <v-row justify="end">
            <v-btn text @click="menu = false" color="warning" v-if="false"
              >Cancel</v-btn
            >
            <v-btn text @click="reset" color="secondary">Reset</v-btn>
            <v-btn color="primary" @click="search">Search</v-btn>
          </v-row>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <v-row justify="end" align="center">
        <v-col class="py-0">
          <v-row justify="start" v-if="selectedFilters.length > 0">
            <v-col cols="auto">
              <v-chip
                v-for="filter in selectedFilters"
                :key="filter.id"
                close
                @click:close="onFilterRemove(filter)"
                class="mr-1"
              >
                {{ filter.label }}:
                <strong>&nbsp;{{ filter.value }}</strong>
              </v-chip>
            </v-col>
          </v-row>
        </v-col>
        <div>
          <v-menu
            offset-x
            :close-on-content-click="false"
            v-model="menu"
            min-width="800px"
          >
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" outlined>
                <v-icon>mdi-magnify</v-icon>
                {{ label }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title
                ><v-icon>mdi-magnify</v-icon>
                Search Params
              </v-card-title>

              <v-divider></v-divider>
              <v-container fluid grid-list-md>
                <v-row align="end">
                  <v-col
                    cols="12"
                    :sm="minColumns"
                    v-for="filter in filtersList"
                    :key="filter.id"
                  >
                    <component
                      :key="filter.id"
                      :is="filter.component"
                      v-bind="filter.props"
                      @onInput="onFilterInput($event, filter.id)"
                      @onClear="onClearFilter($event, filter.id)"
                    ></component>
                  </v-col>
                </v-row>
              </v-container>

              <v-card-actions class="grey lighten-4">
                <custom-list-filters
                  :current-filters="selectedFilters"
                  :ref-name="refName"
                  @load-filters="onLoadFilters"
                ></custom-list-filters>
                <v-spacer></v-spacer>
                <v-btn text @click="menu = false" color="warning">Close</v-btn>
                <v-btn text @click="reset" color="secondary">Reset</v-btn>
                <v-btn color="primary" @click="search">Search</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>
      </v-row>
    </template>
    <div v-if="debug"></div>
  </v-container>
</template>

<script>
import CustomListFilters from "../CustomListFilters.vue";
const randomize = require("randomatic");

export default {
  components: { CustomListFilters },
  name: "ListFilters",

  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    searchButtonId: {
      type: String,
      default: "listSearchBtn",
    },
    label: {
      type: String,
      default: "item",
    },
    //unique key name for caching
    refName: {
      type: String,
      default: "",
    },
    cachedFilters: {
      type: Boolean,
      default: () => true,
    },
    expansionPanel: {
      type: Boolean,
      default: () => true,
    },
    showLabel: {
      type: Boolean,
      default: () => false,
    },
    cols: {
      type: Number,
      default: () => 3,
    },
    context: {
      type: Object,
      default: () => {},
    },
    minColumns: {
      type: Number,
      default: () => 3,
    },
  },

  data() {
    // An empty cached list (stored after a search with all filters cleared)
    // must not shadow the configured defaults — fall back to them (#10962).
    const cached =
      this.refName != "" && this.cachedFilters
        ? this.$store.getters.localSettings(this.refName, [])
        : [];
    return {
      debug: false,
      panel: [],
      menu: false,
      selectedFilters:
        cached && cached.length > 0 ? cached : this.getDefaultFilters(),
    };
  },
  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    filter() {
      return this.selectedFilters.reduce((f, sf) => {
        return { ...f, ...sf.filter };
      }, {});
    },
    filtersList() {
      const filters = [...this.filters];
      const cachedFilters = this.$store.getters.localSettings(this.refName, []);
      for (const filter of filters) {
        const cached = cachedFilters.find(
          (cf) => cf.filterName == filter.props.filterName
        );
        if (cached) {
          //console.log("setting filter", cached);
          filter.props.filterValue = { ...cached.filter };
        } else {
          //reset or set default value
          filter.props.filterValue = filter.props.defaultValue
            ? { ...filter.props.defaultValue }
            : null;
        }
      }
      return filters;
    },
  },
  methods: {
    getDefaultFilters() {
      return this.filters
        .filter((f) => f.defaultValue)
        .map((f) => ({
          id: f.id,
          filterName: f.props.filterName,
          filter: f.defaultValue,
          label: f.props.label,
          value: f.defaultLabel,
        }));
    },
    onFilterInput(payload, id) {
      // update selectedFilters
      const filterData = this.filtersList.find((f) => f.id == id);
      if (payload.displayValue != null) {
        this.selectedFilters = [
          ...this.selectedFilters.filter(
            (f) => f.filterName != filterData.props.filterName
          ),
          {
            id,
            filterName: filterData.props.filterName,
            filter: { ...payload.filter },
            label: payload.displayName,
            value: payload.displayValue,
          },
        ];
      }
    },
    onClearFilter(payload, id) {
      // update selectedFilters
      const filterData = this.filtersList.find((f) => f.id == id);
      this.selectedFilters = [
        ...this.selectedFilters.filter(
          (f) => f.filterName != filterData.props.filterName
        ),
      ];
      //update filter object
      this.$emit("searchParamsChanged", this.filter);
      if (this.cachedFilters) {
        this.storeSettings();
      }
    },
    search(close = true) {
      this.$emit("searchParamsChanged", this.filter);
      this.menu = false;
      if (this.cachedFilters) {
        this.storeSettings();
      }
      if (close) {
        this.panel = [];
      }
    },
    reset() {
      this.selectedFilters = [];
      this.search(false);
    },
    onFilterRemove(filterToRemove) {
      //refresh chips component
      this.selectedFilters = [
        ...this.selectedFilters.filter(
          (f) => f.filterName != filterToRemove.filterName
        ),
      ];
      this.search();
    },
    storeSettings() {
      if (this.refName != "") {
        this.$store.dispatch("updateLocalSettings", {
          [this.refName]: this.selectedFilters,
        });
      }
    },
    keyUpHandler(event) {
      if (event.code == "Enter") {
        this.search();
      }
    },
    onLoadFilters(payload) {
      this.selectedFilters = [...payload];
      this.search();
    },
  },
  mounted() {
    if (this.selectedFilters.length > 0)
      this.$emit("searchParamsChanged", this.filter);
  },
  created() {
    window.addEventListener("keyup", this.keyUpHandler);
  },
  beforeDestroy() {
    window.removeEventListener("keyup", this.keyUpHandler);
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/shared/list-filters/ListFilters.vue